import { useState, useEffect, useRef } from 'react';
import GlobalStateContext from './GlobalStateContext';
import GlobalState from '../interfaces/GlobalStateInterface';
import { EOAListItem, UserDetails, AuthenticatedUser } from '../interfaces/UserDetailsInterface';
import { useAuth0 } from '@auth0/auth0-react';
import { getUserDetails, getEOAs, getEOAPubNubToken } from '../services/userService';
import { getSchools, getDemographicCategories } from '../services/uploadService';
import { getSchoolsConfig } from '../services/eoaConfigServices';
import { Outlet } from 'react-router-dom';
import {
  DATA_QC_SCHOOL_LOCAL_STORAGE_KEY,
  TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY,
  USER_ROLES,
  USER_ROLES_LABELS,
  DISTRICT_LOCAL_STORAGE_KEY,
  TRANSCRIPTS_CATEGORIES,
} from '../constants';
import useErrorModal from '../components/hooks/useErrorModal';
import { AxiosError } from 'axios';
import { calculateUploadProgress } from '../components/overview/overviewPageConfig';
import { getUploadRequirementsData } from '../services/uploadRequirementsService';
import { CategoriesWithValues } from '../interfaces/TranscriptsInterface';
import { getValidPersistedEoaId, persistEoaSelection } from '../utils/eoaPreference';
import { hasSelectedEoa } from '../components/district-selection/districtSelectionUtils';

export const getRoleLabel = (role: string): string => USER_ROLES_LABELS[role] ?? role;

interface AuthErrorShape {
  error?: string;
}

// OIDC spec code + Auth0 SDK-internal code for expired/missing refresh token
const SESSION_EXPIRED_CODES = ['login_required', 'missing_refresh_token'];

const isSessionExpiredError = (error: unknown): boolean => {
  const authError = error as AuthErrorShape;
  return SESSION_EXPIRED_CODES.includes(authError?.error ?? '') || (error as AxiosError)?.response?.status === 401;
};

const GlobalStateContextProvider = () => {
  const [userDetails, setUserDetails] = useState<GlobalState['userDetails']>({
    isPending: true,
    error: null,
    data: undefined,
  });

  const [allEoas, setAllEoas] = useState<GlobalState['allEoas']>({
    isPending: true,
    error: null,
    data: undefined,
  });

  const [requiresDistrictSelection, setRequiresDistrictSelection] = useState(false);

  const [schools, setSchools] = useState<GlobalState['schools']>({
    isPending: true,
    error: null,
    data: undefined,
  });

  const [demographicCategories, setDemographicCategories] = useState<GlobalState['demographicCategories']>({
    isPending: true,
    error: null,
    data: undefined,
  });

  const [schoolsConfig, setSchoolsConfig] = useState<GlobalState['schoolsConfig']>({
    isPending: true,
    error: null,
    data: undefined,
  });

  const [transcriptSelectionSchool, setTranscriptSelectionSchool] = useState('');
  const [dataQcSchool, setDataQcSchool] = useState(localStorage.getItem(DATA_QC_SCHOOL_LOCAL_STORAGE_KEY) || '');

  const [uploadProgressSummary, setUploadProgressSummary] = useState<GlobalState['uploadProgressSummary']>({
    isPending: true,
    error: false,
    data: {
      pending: [],
      progress: [],
      completed: [],
    },
  });

  const [uploadProgressByCategory, setUploadProgressByCategory] = useState({});

  const [schoolsUploadProgressByCategory, setSchoolsUploadProgressByCategory] = useState({});

  const [uploadRequirements, setUploadRequirements] = useState<GlobalState['uploadRequirements']>([]);

  const [pubNubToken, setPubNubToken] = useState('');

  const bootstrapUserRef = useRef<AuthenticatedUser | null>(null);

  let lastNavigatedTranscriptSelectionPage = useRef<CategoriesWithValues<number>>({
    [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 1,
    [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 1,
    [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 1,
  });

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const updatePubNubToken = async (eoaId: string) => {
    try {
      const access_token = await getAccessTokenSilently();
      const response = await getEOAPubNubToken(eoaId, access_token);
      setPubNubToken(response.data.token);
    } catch (error) {
      if (isSessionExpiredError(error)) {
        loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        return;
      }
      handleOpenErrorModal('Error occurred while fetching pubnub token!', error as Error);
    }
  };

  useEffect(() => {
    fetchGlobalStateData();
  }, []);

  useEffect(() => {
    if (!userDetails.data || !hasSelectedEoa(userDetails.data)) return;

    let refreshTokenInterval = setInterval(
      () => {
        updatePubNubToken(userDetails.data!.eoaId);
      },
      1000 * 60 * 15
    ); // 15 minutes

    return () => {
      clearInterval(refreshTokenInterval);
    };
  }, [userDetails.data?.eoaId]);

  const fetchEoaSpecificData = (userDetails: UserDetails) => {
    fetchSchoolsData(userDetails);
    fetchDemographicCategoriesData(userDetails);
    fetchSchoolsConfigData(userDetails);
    const hasAccessToDataUpload =
      userDetails.role === USER_ROLES.DISTRICT_DATA_LEAD || userDetails.role === USER_ROLES.XQ_ADMIN;
    if (hasAccessToDataUpload) {
      handleFetchUploadProgress(userDetails);
    }
  };

  const buildUserDetails = (bootstrapUser: AuthenticatedUser, selectedEoa: EOAListItem): UserDetails => ({
    name: bootstrapUser.name,
    eoaId: selectedEoa.id,
    role: selectedEoa.user_roles[0],
    roleDescription: getRoleLabel(selectedEoa.user_roles[0]),
    access_token: bootstrapUser.access_token,
    district: selectedEoa.district,
    graduatingClass: selectedEoa.year_ending,
    id: bootstrapUser.id,
  });

  const applyEoaSelection = async (selectedEoa: EOAListItem, bootstrapUser: AuthenticatedUser) => {
    const nextUserDetails = buildUserDetails(bootstrapUser, selectedEoa);

    const pubNubTokenResponse = await getEOAPubNubToken(selectedEoa.id, bootstrapUser.access_token);
    setPubNubToken(pubNubTokenResponse.data.token);

    persistEoaSelection(selectedEoa.id);
    localStorage.setItem(DISTRICT_LOCAL_STORAGE_KEY, selectedEoa.district.name);

    setRequiresDistrictSelection(false);
    setUserDetails({ isPending: false, error: null, data: nextUserDetails });
    fetchEoaSpecificData(nextUserDetails);
  };

  const isE2ETestUser = (email: string) => email.startsWith('e2e') && email.endsWith('@test.com');

  const fetchGlobalStateData = async () => {
    try {
      const access_token = await getAccessTokenSilently();

      const userDetailsResponse = await getUserDetails(access_token);
      const includeTestEoas = isE2ETestUser(userDetailsResponse.data.email);
      const eoasResponse = await getEOAs(access_token, includeTestEoas);

      const availableEoas = eoasResponse.data.eoas;

      if (availableEoas.length === 0) {
        throw new Error('No EOAs found for this user');
      }

      const bootstrapUser: AuthenticatedUser = {
        id: userDetailsResponse.data.id,
        name: userDetailsResponse.data.name,
        access_token,
      };
      bootstrapUserRef.current = bootstrapUser;

      setAllEoas({ isPending: false, error: null, data: availableEoas });

      const shouldPromptForDistrict = availableEoas.length > 1;
      const persistedEoaId = shouldPromptForDistrict ? getValidPersistedEoaId(availableEoas) : null;
      const selectedEoa =
        availableEoas.find((eoa) => eoa.id === persistedEoaId) ??
        (shouldPromptForDistrict ? undefined : availableEoas[0]);

      if (!selectedEoa) {
        setRequiresDistrictSelection(true);
        setSchools({ isPending: false, error: null, data: undefined });
        // Store only name/id until the user picks a district; eoaId and role
        // remain absent. hasSelectedEoa() guards every EOA-specific code path.
        setUserDetails({
          isPending: false,
          error: null,
          data: bootstrapUser as UserDetails,
        });
        return;
      }

      await applyEoaSelection(selectedEoa, bootstrapUser);
    } catch (error) {
      if (isSessionExpiredError(error)) {
        loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        return;
      }

      setUserDetails({ isPending: false, error: error, data: undefined });
      setAllEoas({ isPending: false, error: error, data: undefined });
      handleOpenErrorModal('Error occurred while fetching user details data!', error as AxiosError);
    }
  };

  const fetchSchoolsData = async (userDetails: UserDetails) => {
    try {
      const schoolsResponse = await getSchools(userDetails.eoaId, userDetails.access_token);
      const schools = schoolsResponse.data;
      let newSelectedSchoolValue = localStorage.getItem(TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY) || '';

      if (!newSelectedSchoolValue || !schools.find((school) => school.id === newSelectedSchoolValue)) {
        newSelectedSchoolValue = schools[0].id;
        localStorage.setItem(TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY, newSelectedSchoolValue);
      }

      setSchools({ isPending: false, error: null, data: schools });
      setTranscriptSelectionSchool(newSelectedSchoolValue);
    } catch (error) {
      setSchools({ isPending: false, error: error, data: undefined });
      handleOpenErrorModal('Error occurred while fetching schools data!', error as AxiosError);
    }
  };

  const fetchDemographicCategoriesData = async (userDetails: UserDetails) => {
    try {
      const demographicCategoriesResponse = await getDemographicCategories(userDetails.eoaId, userDetails.access_token);
      const categories = demographicCategoriesResponse.data;

      setDemographicCategories({ isPending: false, error: null, data: categories });
    } catch (error) {
      setDemographicCategories({ isPending: false, error: error, data: undefined });
      handleOpenErrorModal('Error occurred while fetching demographic categories data!', error as AxiosError);
    }
  };

  const fetchSchoolsConfigData = async (userDetails: UserDetails) => {
    try {
      const schoolsConfigResponse = await getSchoolsConfig(userDetails.eoaId, userDetails.access_token);

      setSchoolsConfig({ isPending: false, error: null, data: schoolsConfigResponse.data });
    } catch (error) {
      setSchoolsConfig({ isPending: false, error: error, data: undefined });
      handleOpenErrorModal('Error occured while fetching schools config data!', error as AxiosError);
    }
  };

  const updateTranscriptSelectionSchool = (newSelectedSchool: string) => {
    localStorage.setItem(TRANSCRIPT_SELECTION_SCHOOL_LOCAL_STORAGE_KEY, newSelectedSchool);
    setTranscriptSelectionSchool(newSelectedSchool);
  };

  const updateDataQcSchool = (newSelectedSchool: string) => {
    localStorage.setItem(DATA_QC_SCHOOL_LOCAL_STORAGE_KEY, newSelectedSchool);
    setDataQcSchool(newSelectedSchool);
  };

  const completeDistrictSelection = async (eoaId: string) => {
    if (!allEoas.data || !bootstrapUserRef.current) return;

    const selectedEoa = allEoas.data.find((eoa) => eoa.id === eoaId);
    if (!selectedEoa) {
      handleOpenErrorModal('Selected EOA not found!', new Error('Selected EOA not found'));
      return;
    }

    try {
      const access_token = await getAccessTokenSilently();
      bootstrapUserRef.current = { ...bootstrapUserRef.current, access_token };
      await applyEoaSelection(selectedEoa, bootstrapUserRef.current);
    } catch (error) {
      if (isSessionExpiredError(error)) {
        loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        return;
      }
      handleOpenErrorModal('Error occurred while selecting district!', error as AxiosError);
    }
  };

  const switchEoa = async (eoaId: string) => {
    if (!allEoas.data || !userDetails.data) return;

    const selectedEoa = allEoas.data.find((eoa) => eoa.id === eoaId);
    if (!selectedEoa) {
      handleOpenErrorModal('Selected EOA not found!', new Error('Selected EOA not found'));
      return;
    }

    try {
      const access_token = await getAccessTokenSilently();
      const bootstrapUser: AuthenticatedUser = {
        id: userDetails.data.id,
        name: userDetails.data.name,
        access_token,
      };

      await applyEoaSelection(selectedEoa, bootstrapUser);
    } catch (error) {
      if (isSessionExpiredError(error)) {
        loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        return;
      }
      handleOpenErrorModal('Error occurred while switching EOA!', error as AxiosError);
    }
  };

  const handleFetchUploadProgress = async (userDetails?: UserDetails) => {
    if (!userDetails) return;

    let result = await getUploadRequirementsData(userDetails.eoaId, userDetails.access_token);
    setUploadRequirements(result);

    let { categoriesByUploadStatus, uploadStatusByCategory, categoryUploadProgressBySchool } = calculateUploadProgress(
      result,
      handleOpenErrorModal,
      schools.data
    );
    setUploadProgressSummary({ isPending: false, error: null, data: categoriesByUploadStatus });
    setUploadProgressByCategory(uploadStatusByCategory);
    setSchoolsUploadProgressByCategory(categoryUploadProgressBySchool);
  };

  return (
    <GlobalStateContext.Provider
      value={{
        userDetails,
        allEoas,
        schools,
        demographicCategories,
        schoolsConfig,
        uploadRequirements,
        uploadProgressSummary,
        uploadProgressByCategory,
        schoolsUploadProgressByCategory,
        refreshUploadProgress: () => handleFetchUploadProgress(userDetails.data),
        transcriptSelectionSchool,
        dataQcSchool,
        updateTranscriptSelectionSchool,
        updateDataQcSchool,
        switchEoa,
        requiresDistrictSelection,
        completeDistrictSelection,
        pubNubToken,
        lastNavigatedTranscriptSelectionPage: lastNavigatedTranscriptSelectionPage,
      }}
    >
      <Outlet />
    </GlobalStateContext.Provider>
  );
};

export default GlobalStateContextProvider;
